<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue'

const { proxy } = getCurrentInstance()
const transactions = ref([])
const loading = ref(true)
const error = ref('')
const copiedId = ref(null)
let copiedTimer = null

const hasRows = computed(() => transactions.value.length > 0)
const rows = computed(() => transactions.value.map((transaction) => ({
    ...transaction,
    view: describe(transaction),
})))

function describe(transaction) {
    const gift = Number(transaction.gift) === 1
    const donation = transaction.item === 'donation'
    const monthly = transaction.payment_type === 'monthly'
    const received = transaction.transaction_role === 'received'

    if (donation) {
        return { kind: 'donation', title: 'Donation', subtitle: 'Thank you for supporting NIGHTRIDERZ', icon: 'mdi-heart' }
    }
    if (received) {
        const buyer = transaction.buyer_persona_name || 'Unknown driver'
        const target = transaction.target_persona_name || 'Unknown driver'
        return {
            kind: 'gift-received',
            title: transaction.item_name || 'Premium',
            subtitle: `Gift from ${buyer}`,
            icon: 'mdi-gift-outline',
            detail: `Applied to ${target} #${transaction.target_persona_id}`,
            zeroAmount: true,
        }
    }
    if (gift) {
        const target = transaction.target_persona_name || 'Unknown driver'
        return {
            kind: 'gift',
            title: transaction.item_name || 'Premium',
            subtitle: `Gifted to ${target}`,
            icon: 'mdi-gift',
            detail: `#${transaction.target_persona_id}`,
        }
    }
    return {
        kind: 'premium',
        title: transaction.item_name || 'Premium',
        subtitle: monthly ? 'Monthly Premium subscription' : 'Premium purchase for your account',
        icon: 'mdi-crown',
    }
}

function formatPrice(transaction, view) {
    const amount = view?.zeroAmount ? 0 : Number(transaction.price)
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: transaction.currency || 'EUR',
    }).format(amount)
}

function formatDate(date) {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })
        .format(new Date(date.replace(' ', 'T') + 'Z'))
}

function statusLabel(status) {
    return { success: 'Complete', failed: 'Sync failed', processing: 'Processing', pending: 'Pending' }[status] || status
}

function statusClass(status) {
    return { success: 'is-success', failed: 'is-failed', processing: 'is-processing', pending: 'is-pending' }[status] || ''
}

async function copyReference(id) {
    if (!id) return
    try {
        await navigator.clipboard.writeText(String(id))
    } catch {
        const textarea = document.createElement('textarea')
        textarea.value = String(id)
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'absolute'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
    }
    copiedId.value = id
    window.clearTimeout(copiedTimer)
    copiedTimer = window.setTimeout(() => {
        if (copiedId.value === id) copiedId.value = null
    }, 1600)
}

onMounted(async () => {
    try {
        const result = await proxy.$api({ serviceName: 'stripeCheckout', methodName: 'getTransactions' })
        if (result?.error) error.value = result.error
        else transactions.value = Array.isArray(result) ? result : []
    } catch (exception) {
        console.error('Could not load transactions:', exception)
        error.value = 'Could not load your transactions.'
    } finally {
        loading.value = false
    }
})
</script>

<template><div class="settings-tab-page">
        <div class="settings-tab-header">
            <div class="header-icon"><v-icon size="28">mdi-receipt-text</v-icon></div>
            <div class="header-content">
                <h3 class="page-title">Transactions</h3>
                <p class="page-subtitle">Your purchases, gifts sent, gifts received, and donations</p>
            </div>
        </div>
        <Transition name="fade" mode="out-in">
            <div v-if="loading" key="loading" class="state-message">
                <v-progress-circular indeterminate size="28" /> Loading transactions…
            </div>
            <v-alert v-else-if="error" key="error" type="error" variant="tonal">{{ error }}</v-alert>
            <div v-else-if="hasRows" key="list" class="section-list">
                <div v-for="transaction in rows" :key="transaction.transaction_id" class="section-row">
                    <div class="section-body tx-body">
                        <div class="tx-row">
                            <div class="tx-icon" :class="transaction.view.kind">
                                <v-icon size="20">{{ transaction.view.icon }}</v-icon>
                            </div>
                            <div class="tx-copy">
                                <div class="tx-title">{{ transaction.view.title }}</div>
                                <div class="tx-subtitle">{{ transaction.view.subtitle }}</div>
                            </div>
                            <div class="tx-amount" :class="{ free: transaction.view.zeroAmount }">
                                {{ formatPrice(transaction, transaction.view) }}
                            </div>
                        </div>
                        <div class="tx-footnote">
                            <span>{{ formatDate(transaction.date) }}</span>
                            <span class="dot">·</span>
                            <span class="tx-status" :class="statusClass(transaction.abby_status)">{{ statusLabel(transaction.abby_status) }}</span>
                            <template v-if="transaction.view.detail">
                                <span class="dot">·</span>
                                <span class="tx-detail">{{ transaction.view.detail }}</span>
                            </template>
                        </div>
                        <button type="button" class="copy-command" :class="{ copied: copiedId === transaction.transaction_id }" @click="copyReference(transaction.transaction_id)">
                            <code>{{ transaction.transaction_id }}</code>
                            <v-icon size="16">{{ copiedId === transaction.transaction_id ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                            <v-tooltip activator="parent" location="top" :text="copiedId === transaction.transaction_id ? 'Copied!' : 'Copy payment reference'" />
                        </button>
                    </div>
                </div>
            </div>
            <div v-else key="empty" class="state-message">
                <v-icon size="42">mdi-receipt-text-outline</v-icon>
                <p class="state-title">No transactions yet</p>
                <p class="state-copy">Premium purchases, gifts, and donations will appear here after checkout.</p>
            </div>
        </Transition>
    </div></template>

<style scoped>
.tx-body {
  display:flex;
  flex-direction:column;
  gap:12px}
.tx-row {
  display:grid;
  grid-template-columns:auto minmax(0,1fr) auto;
  gap:12px;
  align-items:center}
.tx-icon {
  display:flex;
  align-items:center;
  justify-content:center;
  width:40px;
  height:40px;
  border-radius:10px;
  background:var(--glass-strong);
  color:var(--ink-soft);
  flex-shrink:0}
.tx-icon.premium, .tx-icon.gift {
  background:var(--accent-gold-bg);
  color:var(--accent-gold)}
.tx-icon.gift-received {
  background:#80cbc41f;
  color:var(--status-teal)}
.tx-icon.donation {
  background:#f48fb11f;
  color:var(--status-pink)}
.tx-copy {
  min-width:0}
.tx-title {
  font-size:.95rem;
  font-weight:700;
  line-height:1.2;
  color:var(--ink)}
.tx-subtitle {
  margin-top:3px;
  font-size:.8rem;
  line-height:1.3;
  color:var(--mute);
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap}
.tx-amount {
  font-size:1.05rem;
  font-weight:700;
  color:var(--accent-gold);
  white-space:nowrap}
.tx-amount.free {
  color:var(--mute)}
.tx-footnote {
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  gap:6px;
  padding-left:52px;
  font-size:.74rem;
  font-weight:600;
  color:var(--mute)}
.dot {
  opacity:.55}
.tx-detail {
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap}
.tx-status.is-success {
  color:var(--status-success-soft)}
.tx-status.is-failed {
  color:var(--status-danger)}
.tx-status.is-processing {
  color:var(--status-info)}
.tx-status.is-pending {
  color:var(--status-warn)}
.copy-command {
  display:flex;
  align-items:center;
  gap:8px;
  margin:0;
  width:100%;
  min-width:0;
  padding:6px 8px;
  border-radius:8px;
  border:1px solid var(--line);
  background:var(--glass);
  color:var(--ink);
  cursor:pointer;
  transition:background .15s ease,border-color .15s ease}
.copy-command:hover {
  background:var(--glass-strong);
  border-color:var(--line-strong)}
.copy-command.copied {
  border-color:#81c78473;
  background:#4caf5014}
.copy-command code {
  flex:1;
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  padding:4px 8px;
  border-radius:6px;
  border:1px solid var(--code-line);
  background:var(--code-bg);
  font-family:Consolas,Courier New,monospace;
  font-size:.78rem;
  font-weight:600;
  line-height:1.3;
  color:var(--code-ink);
  text-align:left}
.copy-command .v-icon {
  flex-shrink:0}
@media (max-width: 560px) {
.tx-row {
    grid-template-columns:auto minmax(0,1fr)}
.tx-amount {
    grid-column:2}
.tx-footnote {
    padding-left:0}

}
</style>
